import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertsTabs } from "@/components/AlertsTabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api, apiWithMeta } from "../lib/api";
import { whenever } from "../lib/format";
import i18n from "../lib/i18n";

interface ArchivedRow {
  id: string;
  sku: string;
  asin: string | null;
  title: string | null;
  kind: string;
  severity: "critical" | "high" | "watch";
  message: string;
  suggestedQty: number | null;
  acknowledgedAt: string;
  acknowledgedByName: string | null;
  note: string;
  canReopen: boolean;
}

const RETENTION_DAYS = 30;

/**
 * Archived alerts (Isaac, 2026-10-13): every alert someone acknowledged, with
 * who and the note they wrote. Kept 30 days, then deleted by the daily purge
 * (the acknowledgement stays in the audit log). Reopen puts the alert back on
 * the Urgent tab if it is still live.
 */
export default function ArchivedAlerts() {
  const { t } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["restock-alerts-archive"],
    queryFn: () =>
      apiWithMeta<ArchivedRow[], { count: number }>("/api/v1/inventory/restock-alerts/archive"),
  });
  const rows = Array.isArray(data?.data) ? data.data : [];
  const reopen = useMutation({
    mutationFn: (sku: string) =>
      api("/api/v1/inventory/restock-alerts/reopen", {
        method: "POST",
        body: JSON.stringify({ sku }),
      }),
    onSuccess: () => {
      for (const key of ["restock-alerts-archive", "restock-alerts", "restock-alert-count"]) {
        qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <h1 className="mb-2 font-semibold text-2xl tracking-tight">{t("alerts:title")}</h1>
      <AlertsTabs />
      <p className="mb-4 text-muted-foreground text-sm">
        {t("alerts:archive.hint", { days: RETENTION_DAYS })}
      </p>
      <Card>
        <CardContent className="p-4">
          {isLoading ? (
            <p className="text-muted-foreground text-sm">{t("common:loading")}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm">{t("alerts:archive.none")}</p>
          ) : (
            rows.map((r) => {
              const expires = new Date(
                new Date(r.acknowledgedAt).getTime() + RETENTION_DAYS * 864e5,
              ).toISOString();
              return (
                <div
                  key={r.id}
                  className="flex flex-wrap items-start gap-x-3 gap-y-1 border-t py-3 first:border-t-0"
                >
                  <Badge variant={r.severity === "critical" ? "destructive" : "warning"}>
                    {t(`alerts:severity.${r.severity}`)}
                  </Badge>
                  <div className="min-w-0 flex-1 basis-64">
                    {r.kind === "account_health" ||
                    r.kind === "seller_feedback" ||
                    r.kind === "order_late" ||
                    r.kind === "reminder" ? (
                      <div className="font-medium">{r.title}</div>
                    ) : (
                      <>
                        <div>
                          <span className="font-mono font-medium">{r.sku}</span>
                          {r.asin && (
                            <span className="ml-2 text-muted-foreground text-xs">{r.asin}</span>
                          )}
                        </div>
                        {r.title && (
                          <div className="truncate text-muted-foreground text-sm">{r.title}</div>
                        )}
                      </>
                    )}
                    <div className="mt-1 text-muted-foreground text-sm">{r.message}</div>
                    <div className="mt-1 text-sm">“{r.note}”</div>
                    <div className="text-muted-foreground text-xs">
                      {t("alerts:acknowledgedBy", {
                        who: r.acknowledgedByName ?? "—",
                        when: whenever(r.acknowledgedAt, locale),
                      })}
                      {" · "}
                      {t("alerts:archive.deletes", { when: whenever(expires, locale) })}
                    </div>
                  </div>
                  {r.canReopen && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      disabled={reopen.isPending}
                      onClick={() => reopen.mutate(r.sku)}
                    >
                      {t("alerts:reopen")}
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
